import { IEquipment, TCreateEquipmentMaintenanceBlankDTO } from '../../../equipment.types.ts'
import useHttpLoader from '../../../../../shared/hooks/useHttpLoader.ts'
import SidePage from '../../../../../shared/SidePage/index.tsx'
import useStateWithChange from '../../../../../shared/hooks/useStateWithChange.ts'
import equipmentUtils from '../../../equipment.utils.ts'
import FormField from '../../../../../shared/Forms/FormField/index.tsx'
import Button from '../../../../../shared/Buttons/Button/index.tsx'
import SeparateLine from '../../../../../shared/Forms/SeparateLine/index.tsx'
import TextArea from '../../../../../shared/Inputs/TextArea/index.tsx'
import formsUtils from '../../../../../shared/Forms/forms.utils.ts'
import {
  IMaintenanceBlankSchema,
  IMaintenanceProcessRule,
} from '../../../../Processes/Maintenance/maintenance.types.ts'
import { useState } from 'react'
import useDroparea from '../../../../../shared/Files/hooks/useDroparea.ts'
import Toggle from '../../../../../shared/Inputs/Toggle/index.tsx'
import DropArea from '../../../../../shared/Files/Droparea/index.tsx'
import PlusIcon from '../../../../../assets/icons/plus.svg?react'
import useInputFile from '../../../../../shared/hooks/useInputFile.ts'
import AttachmentLine from '../../../../../shared/Files/Attachment/AttachmentLine/index.tsx'
import BasicTable from '@/shared/Tables/BasicTable/index.tsx'
import css from './createPlannedMaintenanceBlankSidePage.module.css'
import CheckedIcon from '@/assets/icons/checked.svg?react'
import equipmentApi from '@/pages/Equipment/equipment.api.ts'
import equipmentEvents from '@/pages/Equipment/events/equipment/equipment.events.ts'
import { produce } from 'immer'

interface IProps {
  open: boolean
  equipmentId: IEquipment['id']
  blankSchema: IMaintenanceBlankSchema
  processRuleId: IMaintenanceProcessRule['id']
  onSuccess: () => void
  onClose: () => void
}

const CreatePlannedMaintenanceBlankSidePage = (props: IProps) => {
  const { wait, loading } = useHttpLoader()

  const [filesBlockVisible, setFilesBlockVisible] = useState(false)
  const [files, setFiles] = useState<File[]>([])

  const [blank, setBlank, handleChange] = useStateWithChange<TCreateEquipmentMaintenanceBlankDTO>({
    values: equipmentUtils.generateEmptyMaintenanceBlankValues(props.blankSchema),
    blankSchemaId: props.blankSchema.id,
    comment: '',
    type: 'PLANNED',
    relatedProcessRuleId: props.processRuleId,
    equipmentId: props.equipmentId,
  })
  const [maintenanceResults, setMaintenanceResults] = useState(new Array(blank.values.length).fill(false))

  const dropAreaCtrl = useDroparea({ onFiles: (files) => setFiles((prev) => [...prev, ...files]) })
  const fileInputHandler = useInputFile({ onFiles: (files) => setFiles((prev) => [...prev, ...files]) })

  const handleBlankValueChange = (index: number) => (val: string, key: string) => {
    updateMaintenanceResult(index, () => val.length > 0)
    setBlank((prev) => ({
      ...prev,
      values: formsUtils.arrays.updateItem(prev.values, { [key]: val } as any, index),
    }))
  }
  const updateMaintenanceResult = (index: number, updater: (currentValue: boolean) => boolean) => {
    setMaintenanceResults((prev) =>
      produce(prev, (draft) => {
        draft[index] = updater(draft[index])
      })
    )
  }
  const renderBlankFields = () => {
    if (!props.blankSchema || !props.blankSchema.fields.length || !props.processRuleId) {
      return null
    }

    return (
      <>
        <div className="flex-lines gap16">
          <h5 className="text-heading-5">Бланк технического обслуживания</h5>
          {props.blankSchema.fields.map((fieldSchema, index) => (
            <div key={fieldSchema.id} className={css.fieldSchemaContainer}>
              <div className="flex-space-between">
                <h3 className="text-heading-5">{fieldSchema.name}</h3>
                <Button
                  disabled={blank.values[index].value.length > 0}
                  size={'default'}
                  type={maintenanceResults[index] ? 'default' : 'primary'}
                  onClick={() => updateMaintenanceResult(index, (v) => !v)}
                  text={maintenanceResults[index] ? 'Выполнено' : 'Выполнить'}
                  icon={<CheckedIcon className={maintenanceResults[index] ? '' : 'text-white'} />}
                />
              </div>
              <BasicTable
                columns={['Содержание работ', 'Технические требования']}
                values={[
                  [
                    <span className="text-nm" style={{ whiteSpace: 'pre-wrap' }}>
                      {fieldSchema.instruction}
                    </span>,
                    <span className="text-nm" style={{ whiteSpace: 'pre-wrap' }}>
                      {fieldSchema.requirements}
                    </span>,
                  ],
                ]}
              />
              <FormField label={'Комментарий'}>
                <TextArea
                  value={blank.values[index].value}
                  name="value"
                  onChange={handleBlankValueChange(index)}
                  placeHolder="Комментарий"
                />
              </FormField>
            </div>
          ))}
        </div>
      </>
    )
  }

  const renderFilesBlock = () => {
    return (
      <div className="flex-lines gap16" style={{ marginTop: '16px', marginBottom: '16px' }}>
        <Button
          size="default"
          type="default"
          text="Добавить файл"
          fullWidth={true}
          icon={<PlusIcon />}
          onClick={fileInputHandler}
        />
        <div>
          {files.map((f, index) => (
            <AttachmentLine
              index={index}
              file={f}
              onDelete={() => setFiles((prev) => formsUtils.arrays.removeItem(prev, index))}
            />
          ))}
        </div>
      </div>
    )
  }

  const handleSave = () => {
    const filledBlankValues = blank.values.map((item) =>
      item.value.length > 0 ? item : { ...item, value: 'Выполнено' }
    )
    wait(equipmentApi.createEquipmentMaintenanceBlank({ ...blank, values: filledBlankValues }, files), (resp) => {
      if (resp.status === 'success') {
        props.onSuccess()
        equipmentEvents.maintenanceBlankCreated(resp.body)
        props.onClose()
      }
    })
  }

  return (
    <SidePage onClose={props.onClose} open={props.open} size="large" style={{ paddingRight: '0' }}>
      <div className="flex-lines gap16" style={{ height: '100%' }}>
        <div className="flex-lines gap16" style={{ paddingRight: '24px' }}>
          <h2 className="text-heading-4">Добавить запись журнала обслуживания</h2>
          <div className={css.descriptionGrid}>
            <p className="text-nm text-secondary">Тип обслуживания:</p>
            <p className="text-nm">Плановое</p>
            <p className="text-nm text-secondary">Вид обслуживания:</p>
            <p className="text-nm">{props.blankSchema.name}</p>
          </div>
          <FormField label="Комментарий" required={true}>
            <TextArea value={blank.comment} name="comment" onChange={handleChange} placeHolder="Введите комментарий" />
          </FormField>
          <SeparateLine />
        </div>

        <div style={{ overflow: 'auto', flex: '1 1 0%', paddingRight: '18px' }}>{renderBlankFields()}</div>
        <div className="flex-lines gap16" style={{ paddingRight: '24px' }}>
          <SeparateLine />
          <DropArea
            onDrop={dropAreaCtrl.handleDrop}
            isActive={dropAreaCtrl.over}
            onDragOver={dropAreaCtrl.handleDragOver}
            onDragEnter={dropAreaCtrl.handleDragEnter}
            onDragLeave={dropAreaCtrl.handleDragLeave}
          >
            <ToggleLine
              text="Прикрепленные файлы"
              active={filesBlockVisible}
              name="filesBlock"
              onChange={setFilesBlockVisible}
            />
            {filesBlockVisible && renderFilesBlock()}
          </DropArea>
          <div style={{ marginTop: 'auto' }}>
            <SeparateLine />
          </div>
          <div className="inline-flex-gap gap8">
            <Button
              size="default"
              type="primary"
              text="Создать"
              onClick={handleSave}
              loading={loading}
              disabled={!maintenanceResults.every(Boolean) || blank.comment.length === 0}
            />
            <Button size="default" type="default" text="Отменить" onClick={props.onClose} disabled={loading} />
          </div>
        </div>
      </div>
    </SidePage>
  )
}

interface IToggleLineProps {
  text: string
  active: boolean
  name: string
  onChange: (val: boolean, name: string) => void
}
const ToggleLine = (props: IToggleLineProps) => {
  return (
    <div className="inline-flex-gap width100 gap16 flex-space-between center">
      <h5 className="text-heading-5">{props.text}</h5>
      <Toggle name={props.name} checked={props.active} onChange={props.onChange} size="large" />
    </div>
  )
}

export default CreatePlannedMaintenanceBlankSidePage
